import {
  ActivityStep,
  Exception,
  ExceptionSourceKey,
  getCurrentDateAndTime,
  ReorderStopsBody,
  Stop,
  StopRegistration,
  StopUpdate,
} from "@cnx/common";
import i18n from "common/i18n";
import api from "services/api";
import { useShippingManagementStore } from "stores/shippingManagement";

class StopService {
  static fetchStopsByResourceCombinationId = async ({
    resourceCombinationId,
    completed,
  }: {
    resourceCombinationId: string | undefined;
    completed: boolean;
  }): Promise<Stop[]> => {
    const { data } = await api.get<Stop[]>("/stops", {
      params: { resourceCombinationId, completed, translation: i18n.language },
    });
    return data;
  };

  static getStopByStopId = async (stopId: string): Promise<Stop> => {
    const { data } = await api.get<Stop>(`/stops/${stopId}`, {
      params: { translation: i18n.language },
    });
    return data;
  };

  static getStopRegistrationList = async (
    stopId: string,
    translation = i18n.language
  ): Promise<StopRegistration[]> => {
    const { data } = await api.get<StopRegistration[]>(
      `/stop-registration/${stopId}`,
      {
        params: { translation },
      }
    );
    return data;
  };

  static updateStopById = async (
    stopId: string,
    update: StopUpdate,
    activityCode: ActivityStep,
    revert?: boolean,
    forceFinish?: boolean
  ): Promise<Stop> => {
    const location = useShippingManagementStore.getState().location;
    const timeUpdate = getCurrentDateAndTime();

    const { data } = await api.patch<Stop>(
      `/stops/${stopId}`,
      {
        update,
        activityCode,
        location,
        timeUpdate,
        forceFinish,
      },
      { params: { revert } }
    );
    return data;
  };

  static reorderStops = async (reorderOptions: ReorderStopsBody) => {
    await api.post("/stops/reorder", reorderOptions);
  };

  static loadTrailerPhoto = async ({
    files,
    contextId,
    resourceCombinationId,
  }: {
    files: Blob[];
    contextId: string;
    resourceCombinationId: string;
  }) => {
    const formData = new FormData();

    files.forEach(file => {
      formData.append("images", file);
    });

    await api.patch("/stops/trailerphoto", formData, {
      params: { contextId, resourceCombinationId },
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  static saveReasonForNoImage = async ({
    stopId,
    reasonForNoImages,
  }: {
    stopId: string;
    reasonForNoImages: string;
  }): Promise<void> => {
    await api.patch(`/stops/${stopId}/no-image-reason`, { reasonForNoImages });
  };

  static getStopExceptions = async ({
    stopId,
    exceptionSourceKey,
  }: {
    exceptionSourceKey: ExceptionSourceKey;
    stopId: string;
  }): Promise<Exception[]> => {
    const { data } = await api.get<Exception[]>(`/stops/${stopId}/exceptions`, {
      params: {
        translation: i18n.language,
        exceptionSourceKey,
      },
    });
    return data;
  };
}

export default StopService;
